import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { components as c } from "@/components";
import { useConsentBodies } from "@/features/consent/api/selectConsentBodies";
import { searchForTemplateAiWizardNavigation } from "@/features/ai-builder/useTemplateAiDraftStore";
import { ConsentsAdminTable } from "@/features/templates/components/ConsentsAdminTable";
import { TemplateStepFooter } from "@/features/templates/components/TemplateStepFooter";
import { useTemplateLocks } from "@/features/templates/hooks/useTemplateLocks";
import { useTemplateNavigationStore } from "@/features/templates/hooks/useTemplateNavigationStore";
import { useFinalizeTemplateWizard } from "@/features/templates/hooks/useFinalizeTemplateWizard";
import { useAuth } from "@/utils/auth";
import { templateSettingsSchema, TrackingType, type TemplateSettingsSchemaT } from "@patient-watch/types";

type ConsentRowT = {
    consent_body_id: string;
    roles?: string[] | null;
    locked?: boolean;
    row_id?: string;
};

type ConsentFormValues = { consents: ConsentRowT[] };

/**
 * Admin-only step, before the hospital number and trackers: which consents patients sign, and in what order.
 */
const TemplateConsentStep = () => {
    const auth = useAuth();
    const navigate = useNavigate();
    const { templateId } = Route.useParams();
    const search = Route.useSearch();
    const locks = useTemplateLocks(templateId);
    const { data: consentBodies = [] } = useConsentBodies({ publishedOnly: true, enabled: true });
    const { currentStep, setCurrentStep, markStepComplete, nextStep, updateEventSteps, getNextStepRoute } =
        useTemplateNavigationStore();
    const { finalize, isSubmitting, isTemplateOwnerLookupBlockingSubmit } = useFinalizeTemplateWizard(
        templateId,
        search as Partial<TemplateSettingsSchemaT>
    );

    const effectiveEvents = useMemo(
        () => (search.required_events ?? []).filter((event) => event !== "skip") as TrackingType[],
        [search.required_events]
    );

    const form = useForm<ConsentFormValues>({
        defaultValues: {
            consents: (search.consents ?? []).map((consent) => ({
                consent_body_id: consent.consent_body_id,
                roles: consent.roles ?? ["patient"],
                locked: consent.locked,
                row_id: consent.row_id,
            })),
        },
    });

    const { fields, append, remove, move } = useFieldArray({ control: form.control, name: "consents" });

    // A reload lands here with only the base steps in the store, so rebuild the steps that follow.
    useEffect(() => {
        updateEventSteps(effectiveEvents);
        setCurrentStep("consent");
    }, [effectiveEvents, updateEventSteps, setCurrentStep]);

    const goNext = form.handleSubmit((values) => {
        const consents = values.consents
            .filter((consent) => consent.consent_body_id)
            .map((consent) => ({
                consent_body_id: consent.consent_body_id,
                roles: consent.roles?.length ? consent.roles : ["patient"],
                locked: consent.locked,
                row_id: consent.row_id,
            }));
        const nextSettings = { ...search, consents } as Partial<TemplateSettingsSchemaT>;

        const route = getNextStepRoute(currentStep, { templateId });
        if (!route) {
            finalize(nextSettings);
            return;
        }
        const payload = searchForTemplateAiWizardNavigation(templateId, auth?.id ?? "", nextSettings);
        if (!payload) return;
        markStepComplete("consent");
        nextStep();
        navigate({ ...route, search: payload as never });
    });

    return (
        <div className="space-y-8">
            <c.ui.Card>
                <c.ui.CardHeader>
                    <c.ui.CardTitle>Consent</c.ui.CardTitle>
                </c.ui.CardHeader>
                <c.ui.CardContent>
                    <c.ui.Form {...form}>
                        <form onSubmit={(event) => event.preventDefault()} className="space-y-4">
                            <div className="flex justify-between items-start gap-4">
                                <div className="space-y-1 min-w-0">
                                    <p className="text-muted-foreground text-sm">
                                        Drag rows to set signing order. Only published consents from the Consent builder
                                        appear here.
                                    </p>
                                </div>
                                <c.fc.AddButton
                                    buttonType="button"
                                    variant="outline"
                                    size="sm"
                                    className="w-fit shrink-0"
                                    label="Add consent"
                                    onClick={() => append({ consent_body_id: "", roles: ["patient"] })}
                                />
                            </div>
                            <ConsentsAdminTable
                                form={form}
                                fields={fields}
                                control={form.control}
                                consentBodies={consentBodies.map((consentBody) => ({
                                    consent_body_id: consentBody.consent_body_id,
                                    consent_name: consentBody.consent_name,
                                }))}
                                onRemove={remove}
                                onMove={move}
                                canConfigureLocks={locks.canConfigureLocks}
                            />
                        </form>
                    </c.ui.Form>
                </c.ui.CardContent>
            </c.ui.Card>

            <TemplateStepFooter
                onPrimaryClick={() => void goNext()}
                isLoadingPrimary={isSubmitting}
                disabledPrimary={isTemplateOwnerLookupBlockingSubmit}
            />
        </div>
    );
};

const consentSearchSchema = templateSettingsSchema.partial().extend({
    required_events: z.array(z.union([TrackingType, z.literal("skip")])).optional(),
});

export const Route = createFileRoute("/_app/templates/$templateId/consent")({
    component: TemplateConsentStep,
    validateSearch: (search) => consentSearchSchema.parse(search),
});
